import React, { useContext, useEffect, useState } from "react";
import { Modal, Select, Row, Col, Form, Upload, Progress } from "antd";
import { ToastContainer, toast } from "react-toastify";
import { AiOutlineClose } from "react-icons/ai";
import { FileFilled } from "@ant-design/icons";
import uploadCloud from "../../assets/svg/uploadCloud.svg";
import useAxiosInstance from "../../lib/useAxiosInstance";
import ReportContext from "../../context/reportContext";

const { Option } = Select;

const AddSocialMedia = ({ isOpen, setShowSMModal }) => {
  const [form] = Form.useForm();
  const axiosInstance = useAxiosInstance();
  const reportContext = useContext(ReportContext);

  const [clients, setClients] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [inputValues, setInputValues] = useState({
    client: null,
    file: null,
    description: "",
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showCompletedProgress, setShowCompletedProgress] = useState(false);

  // Fetch all clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await axiosInstance.get("api/clients/getAllClients");
        if (res?.data?.data) setClients(res.data.data);
      } catch (error) {
        console.error("Failed to fetch clients:", error);
        toast.error("Failed to fetch clients");
      }
    };
    fetchClients();
  }, []);

  const closeModal = () => {
    reportContext.updateOpenModal(false);
    form.resetFields();
    setShowSMModal(false);
    handleResetFile();
    setUploadProgress(0);
    setShowCompletedProgress(false);
  };

  const handleResetFile = () => {
    setFileList([]);
    setInputValues((prev) => ({ ...prev, file: null }));
    setUploadProgress(0);
    setShowCompletedProgress(false);
  };

  const handleChange = (value, name) => {
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitForm = async () => {
    if (!inputValues.client || !fileList.length || !inputValues.description.trim()) {
      toast.error("Please select a client, add a description, and upload a file");
      return;
    }

    try {
      // Step 1: Create Social Media entry first
      const formData = new FormData();
      formData.append("file", fileList[0].originFileObj);
      formData.append("client",inputValues.client)
      formData.append("description", inputValues?.description); 
      const createResponse = await axiosInstance.post("api/social-media/createSM", 
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        })

      const smId = createResponse?.data?.data?._id;
    
      if (!smId) {
        toast.error("Failed to create Social Media entry.");
        return;
      }
    formData.append("smId", smId); 
      // Step 2: Upload file with progress
      const uploadResponse = await axiosInstance.post(
        "api/social-media/addSMData",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (uploadResponse?.data?.data?.[0]?.filePath) {
        setShowCompletedProgress(true);
        toast.success("Social Media entry created and file uploaded successfully!");
        closeModal();
      } else {
        toast.error("Upload failed. File path missing from response.");
      }
      // inputValues.append("smId", smId); 
      await axiosInstance.post(
        "api/social-media/createSMSummary",
        {inputValues,smId}
      );

    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      {isOpen && (
        <Modal
          className="add-user-modal"
          open={isOpen}
          onCancel={closeModal}
          width={800}
          maskClosable={false}
          footer={[
            <button
              key="add-social-media"
              form="social-media-form"
              type="submit"
              className="add-user-btn"
              style={{ width: "auto" }}
              onClick={handleSubmitForm}
            >
              Upload Social Media Data
            </button>,
          ]}
        >
          <ToastContainer />
          <Form form={form} id="social-media-form" className="modal-body-container">
            <div className="user-info-modal">
              <div className="modal-title">
                <h3>Add Social Media Data</h3>
              </div>
            </div>

            <div className="modal-wrapper-body constituency-form">
              <div className="model-wrapper">
                <div className="modal-wrapper-content">
                  <h4>Upload Information</h4>

                  {/* CLIENT SELECTION */}
                  <Row className="add-modal-row mt-5 mb-2">
                    <Col span={18}>
                      <label>
                        Select Client<span className="required-field">*</span>
                      </label>
                      <Form.Item
                        name="client"
                        rules={[{ required: true, message: "Please select a client" }]}
                      >
                        <Select
                          showSearch
                          onChange={(value) => handleChange(value, "client")}
                          placeholder="Select Client"
                          optionFilterProp="children"
                        >
                          {clients.map((client) => (
                            <Option key={client._id} value={client._id}>
                              {client?.adminFirstName} {client?.adminLastName}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* DESCRIPTION */}
                  <Row className="add-modal-row mt-3 mb-2">
                    <Col span={18}>
                      <label>
                        Description<span className="required-field">*</span>
                      </label>
                      <Form.Item
                        name="description"
                        rules={[{ required: true, message: "Please enter a description" }]}
                      >
                        <textarea
                          className="w-full border border-gray-300 rounded-md p-2"
                          rows={3}
                          placeholder="Enter a short description about this upload..."
                          value={inputValues.description}
                          onChange={(e) => handleChange(e.target.value, "description")}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* EXCEL UPLOAD */}
                  <Row className="add-modal-row mb-2">
                    <Col span={22}>
                      <h3>
                        Upload Excel / CSV File
                        <span className="required-field">*</span>
                      </h3>
                      <Form.Item
                        name="file"
                        rules={[{ required: true, message: "Please upload a file" }]}
                      >
                        <div className="constituency-upload-file">
                          <Upload.Dragger
                            key="select-file"
                            name="file"
                            beforeUpload={() => false}
                            fileList={fileList}
                            onChange={(info) => {
                              const { fileList } = info;
                              setFileList(fileList);
                              handleChange(fileList[0], "file");
                            }}
                            className="custom-file-upload"
                            accept=".xlsx,.xls,.csv"
                          >
                            {fileList.length > 0 ? (
                              <div className="fileUpload">
                                <div className="text-right">
                                  <button
                                    onClick={handleResetFile}
                                    className="reset-button font-bold"
                                  >
                                    <AiOutlineClose />
                                  </button>
                                </div>
                                <div className="flex file-icon">
                                  <FileFilled />
                                  <p>{fileList[0]?.name}</p>
                                </div>
                                {uploadProgress > 0 && !showCompletedProgress && (
                                  <Progress percent={uploadProgress} />
                                )}
                                {showCompletedProgress && <Progress percent={100} status="success" />}
                              </div>
                            ) : (
                              <div>
                                <img src={uploadCloud} alt="" className="m-auto" />
                                <p className="ant-upload-text">
                                  Select a file or drag and drop
                                </p>
                                <p className="ant-upload-text">
                                  File must be .xlsx, .xls or .csv (Max 10MB)
                                </p>
                                <span className="select-file">Select File</span>
                              </div>
                            )}
                          </Upload.Dragger>
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default AddSocialMedia;
